import { useEffect, useRef, useState } from "react";
import {
  Activity,
  HeartPulse,
  Stethoscope,
  Thermometer,
  Wind,
  Download,
  AlertTriangle,
  CheckCircle2,
  Video,
  Camera,
} from "lucide-react";
import { api } from "../lib/api";

const TYPE_ICON = {
  BP: HeartPulse,
  ECG: Activity,
  SpO2: Wind,
  Stethoscope: Stethoscope,
  Temperature: Thermometer,
  Otoscope: Video,
  "Otoscope Examination": Video,
  "Otoscope - Left Ear": Video,
  "Otoscope - Right Ear": Video,
  "Otoscope - Throat/Nasal": Video,
};

const STATUS_BADGE = {
  pairing: "bg-amber-50 text-amber-700",
  running: "bg-amber-50 text-amber-700",
  completed: "bg-emerald-50 text-emerald-700",
  failed: "bg-rose-50 text-rose-700",
};

export default function DiagnosticTestsLive({ fetchPath, intervalMs = 2000 }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewMedia, setPreviewMedia] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(fetchPath);
        if (!cancelled) {
          setTests(data.items || []);
          setLoading(false);
        }
      } catch (_e) {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    timerRef.current = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, [fetchPath, intervalMs]);

  const openReport = async (testId) => {
    try {
      const res = await api.get(`/diagnostic-tests/${testId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (_e) {
      /* silent */
    }
  };

  return (
    <section
      data-testid="diagnostic-tests-live"
      className="border border-border rounded-xl p-3 bg-secondary/30 space-y-2"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-primary">
            Live diagnostic reports
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Streamed in real-time from the clinic.
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-12 bg-muted rounded-lg animate-pulse" />
      ) : tests.length === 0 ? (
        <div
          data-testid="diagnostic-tests-empty"
          className="text-xs text-muted-foreground bg-white border border-border rounded-lg p-3 text-center"
        >
          No diagnostic tests yet. Clinic will run them in-room.
        </div>
      ) : (
        <ul className="space-y-2">
          {tests.map((t) => {
            const Icon = TYPE_ICON[t.test_type] || Activity;
            const abnormal = t.summary?.abnormal_flag;
            const isRunning = t.status === "pairing" || t.status === "running";
            return (
              <li
                key={t.id}
                data-testid={`live-test-card-${t.id}`}
                className="bg-white border border-border rounded-xl p-3"
              >
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{t.test_type}</span>
                      <span
                        className={
                          "text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full " +
                          (STATUS_BADGE[t.status] || "bg-muted text-muted-foreground")
                        }
                      >
                        {t.status}
                      </span>
                      {t.status === "completed" && (
                        <span
                          data-testid={`live-test-flag-${t.id}`}
                          className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 bg-emerald-100 text-emerald-700"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          Vitals Recorded
                        </span>
                      )}
                    </div>
                    {t.status === "completed" && t.summary?.headline && (
                      <div
                        data-testid={`live-test-headline-${t.id}`}
                        className={"text-sm mt-1 " + (abnormal ? "text-rose-700 font-semibold" : "text-foreground")}
                      >
                        {t.summary.headline}
                      </div>
                    )}
                    {t.status === "completed" && (t.test_type === "ECG" || t.test_type === "12-Lead ECG") && (
                      <EcgCardGraph test={t} openReport={openReport} />
                    )}
                    {t.status === "completed" && (t.test_type?.toUpperCase().includes("STETH") || t.test_type === "STETHOSCOPE" || t.test_type === "Stethoscope") && (() => {
                      const backendBase = (api.defaults?.baseURL || "").replace(/\/api$/, "");
                      const directApiUrl = `${backendBase}/api/diagnostic-tests/${t.id}/audio`;
                      const downloadApiUrl = `${backendBase}/api/diagnostic-tests/${t.id}/audio?download=1`;
                      const audioSrc = t.result_summary?.audio_url || t.payload?.audio_url || (t.result_summary?.audio_base64 ? `data:audio/wav;base64,${t.result_summary.audio_base64}` : (t.payload?.audio_base64 ? `data:audio/wav;base64,${t.payload.audio_base64}` : directApiUrl));
                      const modeLabel = t.result_summary?.mode || t.payload?.mode || "HEART";
                      const posLabel = t.result_summary?.position || t.payload?.position || "Chest";
                      const durLabel = t.result_summary?.duration || t.payload?.duration || t.result_summary?.result1 || t.payload?.result1 || 15;
                      const headline = typeof (t.result_summary?.headline || t.summary?.headline || t.payload?.headline) === 'string'
                        ? (t.result_summary?.headline || t.summary?.headline || t.payload?.headline)
                        : (typeof t.summary === 'string' ? t.summary : '');
                      const takeMatch = String(headline).match(/Take\s*(\d+)/i);
                      const takeNumber = t.result_summary?.take || t.payload?.take || (takeMatch ? takeMatch[1] : 1);
                      const dateTime = t.result_summary?.dateTime || t.payload?.dateTime || t.completed_at || t.started_at || "";

                      const handleDownload = (e) => {
                        e.preventDefault();
                        const b64 = t.result_summary?.audio_base64 || t.payload?.audio_base64;
                        if (b64) {
                          const a = document.createElement("a");
                          a.href = `data:audio/wav;base64,${b64}`;
                          a.download = `stethoscope_${t.id}.wav`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } else {
                          window.open(downloadApiUrl, "_blank");
                        }
                      };

                      return (
                        <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2">
                          <div className="text-[11px] font-bold text-emerald-900 flex items-center justify-between flex-wrap gap-1">
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span>🔊 Listen to Stethoscope Audio ({modeLabel} — {posLabel})</span>
                              <span className="text-[10px] bg-emerald-700 text-white px-2 py-0.5 rounded-full font-bold">
                                Take {takeNumber}
                              </span>
                            </span>
                            <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-mono font-bold">
                              {durLabel}s Track
                            </span>
                          </div>
                          {dateTime && (
                            <div className="text-[10px] text-emerald-700">
                              📅 Recorded: {dateTime}
                            </div>
                          )}
                          <audio
                            controls
                            preload="auto"
                            src={audioSrc}
                            className="w-full h-8 rounded mt-1"
                          />
                          <div className="pt-1.5 border-t border-emerald-200/60 flex items-center justify-between text-xs">
                            <button
                              type="button"
                              onClick={handleDownload}
                              className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0"
                            >
                              <Download className="h-3 w-3" /> Download Audio (.wav)
                            </button>
                            <a
                              href={directApiUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] font-bold text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1"
                            >
                              🔗 Direct Stream URL (.wav)
                            </a>
                          </div>
                        </div>
                      );
                    })()}
                    {t.status === "completed" && (t.test_type?.toUpperCase().includes("OTOSCOPE") || t.category === "otoscope" || t.test_type?.toUpperCase().includes("EAR")) && (() => {
                      const backendBase = (api.defaults?.baseURL || "").replace(/\/api$/, "");
                      const directApiUrl = `${backendBase}/api/diagnostic-tests/${t.id}/media`;
                      const downloadApiUrl = `${backendBase}/api/diagnostic-tests/${t.id}/media?download=1`;
                      const capType = String(t.result_summary?.capture_type || t.payload?.capture_type || "").toLowerCase();
                      const fileName = String(t.result_summary?.file_name || t.payload?.file_name || "").toLowerCase();
                      const summaryStr = typeof t.summary === "string" ? t.summary : String(t.summary?.headline || t.result_summary?.headline || t.payload?.headline || "");
                      const isVideo = capType === "video" || summaryStr.toLowerCase().includes("video") || fileName.endsWith(".mp4");
                      const b64 = t.result_summary?.media_base64 || t.payload?.media_base64 || (typeof t.summary === "object" ? t.summary?.media_base64 : "");
                      const rawUrl = t.result_summary?.media_url || t.payload?.media_url || "";
                      const mediaSrc = b64
                        ? (isVideo ? `data:video/mp4;base64,${b64}` : `data:image/jpeg;base64,${b64}`)
                        : (rawUrl ? (rawUrl.startsWith("http") ? rawUrl : `${backendBase}${rawUrl}`) : directApiUrl);
                      const earSide = t.result_summary?.ear_side || t.payload?.ear_side || t.payload?.parameters?.['Examination Site'] || t.payload?.parameters?.['Ear / Site Examined'] || 'Ear Canal';
                      const duration = t.result_summary?.duration || t.payload?.duration || t.payload?.parameters?.['Duration'] || '';
                      const dateTime = t.result_summary?.dateTime || t.payload?.dateTime || t.result_summary?.timestamp || t.completed_at || t.started_at || '';

                      const handleDownload = (e) => {
                        e.preventDefault();
                        if (b64) {
                          const a = document.createElement("a");
                          a.href = isVideo ? `data:video/mp4;base64,${b64}` : `data:image/jpeg;base64,${b64}`;
                          a.download = fileName || (isVideo ? `otoscope_${t.id}.mp4` : `otoscope_${t.id}.jpg`);
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        } else {
                          window.open(downloadApiUrl, "_blank");
                        }
                      };

                      return (
                        <div className="mt-2 p-3 bg-sky-50 border border-sky-200 rounded-xl space-y-2">
                          <div className="text-[11px] font-bold text-sky-900 flex items-center justify-between flex-wrap gap-1">
                            <span className="flex items-center gap-1.5 flex-wrap">
                              <span>{isVideo ? '📹 Otoscopic Video Recording' : '📷 Otoscopic HD Snapshot'} ({earSide})</span>
                              <span className="text-[10px] bg-sky-700 text-white px-2 py-0.5 rounded-full font-bold">
                                {isVideo ? 'Video' : 'Photo'}
                              </span>
                            </span>
                            {duration && duration !== 'N/A' && duration !== 'Snapshot' && (
                              <span className="text-[10px] bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full font-mono font-bold">
                                {duration}
                              </span>
                            )}
                          </div>
                          {dateTime && (
                            <div className="text-[10px] text-sky-700 font-medium">
                              📅 Recorded: {dateTime}
                            </div>
                          )}
                          
                          {isVideo ? (
                            <div className="relative rounded-lg overflow-hidden bg-black mt-1">
                              <video
                                controls
                                preload="metadata"
                                src={mediaSrc}
                                className="w-full max-h-64 object-contain"
                              />
                            </div>
                          ) : (
                            <div 
                              onClick={() => setPreviewMedia({ isVideo: false, src: mediaSrc, title: `Otoscope Snapshot (${earSide})`, dateTime })}
                              className="mt-1 rounded-lg overflow-hidden bg-slate-900 border border-sky-200 max-h-64 flex items-center justify-center cursor-pointer group relative"
                            >
                              <img
                                src={mediaSrc}
                                alt="Otoscope capture"
                                className="max-h-60 object-contain rounded-lg group-hover:scale-105 transition-transform"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1">
                                🔍 Click to Zoom Preview
                              </div>
                            </div>
                          )}

                          <div className="pt-1.5 border-t border-sky-200/60 flex items-center justify-between text-xs flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={handleDownload}
                                className="text-[11px] font-bold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0"
                              >
                                <Download className="h-3 w-3" /> Download {isVideo ? 'Video (.mp4)' : 'Image (.jpg)'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setPreviewMedia({ isVideo, src: mediaSrc, title: `Otoscope ${isVideo ? 'Video' : 'Snapshot'} (${earSide})`, dateTime, downloadUrl: downloadApiUrl })}
                                className="text-[11px] font-bold text-sky-700 hover:text-sky-900 underline inline-flex items-center gap-1 bg-transparent border-0 cursor-pointer p-0"
                              >
                                👁 Preview Full Size
                              </button>
                            </div>
                            <a
                              href={directApiUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] font-bold text-sky-700 hover:text-sky-900 underline inline-flex items-center gap-1"
                            >
                              🔗 Direct Stream URL
                            </a>
                          </div>
                        </div>
                      );
                    })()}
                    {isRunning && (
                      <div className="mt-1.5">
                        <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                          <div
                            data-testid={`live-test-progress-${t.id}`}
                            className="h-full bg-primary transition-[width] duration-500"
                            style={{ width: `${t.progress_percent || 0}%` }}
                          />
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {t.progress_percent || 0}% · {t.device_name}
                        </div>
                      </div>
                    )}
                  </div>
                  {t.status === "completed" && (
                    <button
                      type="button"
                      onClick={() => openReport(t.id)}
                      data-testid={`live-test-view-${t.id}`}
                      className="text-xs font-medium text-primary inline-flex items-center gap-1 hover:underline shrink-0"
                    >
                      <Download className="h-3.5 w-3.5" /> View report
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* FULL SCREEN MEDIA PREVIEW MODAL */}
      {previewMedia && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <div 
            className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3.5 bg-slate-800 border-b border-slate-700">
              <div>
                <div className="text-white text-sm font-bold">{previewMedia.title}</div>
                <div className="text-slate-400 text-xs">{previewMedia.dateTime}</div>
              </div>
              <button 
                onClick={() => setPreviewMedia(null)}
                className="text-slate-400 hover:text-white text-lg font-bold px-2.5 py-1 rounded-lg bg-slate-700/60"
              >
                ✕
              </button>
            </div>
            <div className="p-4 bg-black flex items-center justify-center min-h-[300px] max-h-[70vh]">
              {previewMedia.isVideo ? (
                <video 
                  controls 
                  autoPlay 
                  src={previewMedia.src} 
                  className="max-h-[65vh] w-full object-contain rounded-lg" 
                />
              ) : (
                <img 
                  src={previewMedia.src} 
                  alt="Full preview" 
                  className="max-h-[65vh] max-w-full object-contain rounded-lg" 
                />
              )}
            </div>
            <div className="p-3 bg-slate-800 border-t border-slate-700 flex items-center justify-between">
              <a 
                href={previewMedia.downloadUrl || previewMedia.src}
                target="_blank"
                download
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90"
              >
                <Download className="h-3.5 w-3.5" /> Download Media
              </a>
              <button
                onClick={() => setPreviewMedia(null)}
                className="px-4 py-2 bg-slate-700 text-white text-xs font-bold rounded-lg hover:bg-slate-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function EcgCardGraph({ test, openReport }) {
  const [modalOpen, setModalOpen] = useState(false);
  const wf = test?.payload?.waveform || [
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
  ];

  const leadsCol1 = ["I", "II", "III"];
  const leadsCol2 = ["aVR", "aVL", "aVF"];
  const leadsCol3 = ["V1", "V2", "V3"];
  const leadsCol4 = ["V4", "V5", "V6"];
  const allColumns = [leadsCol1, leadsCol2, leadsCol3, leadsCol4];

  return (
    <div className="mt-2.5 relative">
      {/* 12-Lead Beige Grid Box matching physical machine report */}
      <div
        onClick={() => setModalOpen(true)}
        className="cursor-pointer border-2 border-amber-300 rounded-xl p-3 bg-[#fefce8] shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group"
        style={{
          backgroundImage:
            "linear-gradient(0deg, #fde047 1px, transparent 1px), linear-gradient(90deg, #fde047 1px, transparent 1px)",
          backgroundSize: "10px 10px",
        }}
      >
        {/* Top-Left Badge */}
        <div className="flex items-center justify-between mb-2">
          <span className="bg-amber-100/90 text-amber-950 font-bold text-[10px] uppercase px-2 py-0.5 rounded border border-amber-300 flex items-center gap-1">
            📈 12-LEAD ECG MONITOR
          </span>
          <span className="text-[10px] font-bold text-amber-900 bg-amber-200/80 px-2 py-0.5 rounded flex items-center gap-1">
            ⤢ Tap for Landscape View
          </span>
        </div>

        {/* 4 Columns 12 Leads Layout */}
        <div className="grid grid-cols-4 gap-2 mb-2">
          {allColumns.map((col, cIdx) => (
            <div key={cIdx} className="space-y-1.5">
              {col.map((lead) => {
                const pts = wf.map((v, i) => {
                  const x = i * (120 / Math.max(wf.length - 1, 1));
                  const y = 18 - v * 10;
                  return `${x.toFixed(1)},${y.toFixed(1)}`;
                }).join(" ");
                return (
                  <div key={lead} className="relative bg-white/50 p-1 rounded border border-amber-200">
                    <span className="font-mono text-[9px] font-bold text-amber-900 block mb-0.5">
                      {lead}
                    </span>
                    <svg viewBox="0 0 120 36" className="w-full h-8">
                      <polyline fill="none" stroke="#dc2626" strokeWidth="1.6" points={pts} />
                    </svg>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Rhythm Strip */}
        <div className="bg-white/50 p-1.5 rounded border border-amber-200">
          <span className="font-mono text-[9px] font-bold text-amber-900 block mb-0.5">
            Lead II Rhythm Strip
          </span>
          <svg viewBox="0 0 500 30" className="w-full h-6">
            <polyline
              fill="none"
              stroke="#dc2626"
              strokeWidth="1.6"
              points={wf.concat(wf).map((v, i) => `${(i * (500 / (wf.length * 2 - 1))).toFixed(1)},${(15 - v * 9).toFixed(1)}`).join(" ")}
            />
          </svg>
        </div>

        {/* Footer info */}
        <div className="flex items-center justify-between text-[9px] font-mono text-amber-900 mt-1.5 pt-1 border-t border-amber-200/80">
          <span>Scale: 25.0 mm/s · 10.0 mm/mV</span>
          <span className="font-bold">Filter: 0.40 Hz</span>
        </div>
      </div>

      {/* Landscape Modal */}
      {modalOpen && <EcgGridModal test={test} onClose={() => setModalOpen(false)} />}
    </div>
  );
}

function EcgGridModal({ test, onClose }) {
  const [rotated, setRotated] = useState(false);
  const wf = test?.payload?.waveform || [
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
    0, 0.05, 0.1, 0.05, 0, -0.1, 0.6, 1.4, -0.4, 0, 0.1, 0.25, 0.15, 0.05, 0,
  ];

  const leadsCol1 = ["I", "II", "III"];
  const leadsCol2 = ["aVR", "aVL", "aVF"];
  const leadsCol3 = ["V1", "V2", "V3"];
  const leadsCol4 = ["V4", "V5", "V6"];
  const allColumns = [leadsCol1, leadsCol2, leadsCol3, leadsCol4];

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col p-4 backdrop-blur-md overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between text-white pb-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <span className="bg-amber-500 text-amber-950 font-bold text-xs px-2.5 py-1 rounded-md">
            📈 12-LEAD ECG LANDSCAPE VIEW
          </span>
          <span className="text-xs text-gray-400">Scale: 25.0 mm/s · 10.0 mm/mV</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setRotated(!rotated)}
            className="bg-gray-800 hover:bg-gray-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5 border border-gray-700"
          >
            🔄 {rotated ? "Portrait" : "Rotate 90° Landscape"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm px-4 py-1.5 rounded-lg"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Main Fullscreen Grid */}
      <div className="flex-1 flex items-center justify-center p-2 overflow-auto">
        <div
          className={`w-full max-w-5xl bg-[#fefce8] border-2 border-amber-300 rounded-xl p-4 shadow-2xl transition-transform duration-300 ${
            rotated ? "rotate-90 scale-110" : ""
          }`}
          style={{
            backgroundImage:
              "linear-gradient(0deg, #fde047 1px, transparent 1px), linear-gradient(90deg, #fde047 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        >
          {/* 4-Column Leads Grid */}
          <div className="grid grid-cols-4 gap-3 mb-3">
            {allColumns.map((col, cIdx) => (
              <div key={cIdx} className="space-y-3">
                {col.map((lead) => {
                  const pts = wf.map((v, i) => {
                    const x = i * (220 / Math.max(wf.length - 1, 1));
                    const y = 25 - v * 15;
                    return `${x.toFixed(1)},${y.toFixed(1)}`;
                  }).join(" ");

                  return (
                    <div key={lead} className="relative bg-white/60 p-2 rounded border border-amber-200">
                      <span className="font-mono font-bold text-[11px] text-amber-900 bg-amber-100/90 px-1.5 rounded block mb-1">
                        {lead}
                      </span>
                      <svg viewBox="0 0 220 50" className="w-full h-14">
                        <polyline fill="none" stroke="#dc2626" strokeWidth="1.8" points={pts} />
                      </svg>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Bottom Lead II Rhythm Strip */}
          <div className="relative bg-white/60 p-2 rounded border border-amber-200">
            <span className="font-mono font-bold text-[11px] text-amber-900 bg-amber-100/90 px-1.5 rounded block mb-1">
              Lead II Continuous Rhythm Strip
            </span>
            <svg viewBox="0 0 800 45" className="w-full h-12">
              <polyline
                fill="none"
                stroke="#dc2626"
                strokeWidth="1.8"
                points={wf.concat(wf).concat(wf).map((v, i) => `${(i * (800 / (wf.length * 3 - 1))).toFixed(1)},${(22.5 - v * 14).toFixed(1)}`).join(" ")}
              />
            </svg>
          </div>

          {/* Scale Legend Footer */}
          <div className="flex items-center justify-between text-[11px] font-mono text-amber-900 mt-2 pt-2 border-t border-amber-200">
            <div>Scale: 25.0 mm/s · 10.0 mm/mV · 5 mm/sqr</div>
            <div>Filter: 0.40 Hz · HR: {test?.payload?.pulse || 75} bpm</div>
          </div>
        </div>
      </div>
    </div>
  );
}
